import React, { useEffect, useState } from 'react'
import { InfoCard } from './InfoCard'
import { DataTable } from './DataTable'
import { PHChart } from './charts/PHChart'
import { TemperatureChart } from './charts/TemperatureChart'
import { supabase } from '@lura/shared'
import type { DeviceDataRow } from '@lura/shared'

interface PHData {
  time: number
  value: number
}

interface TemperatureData {
  time: number
  value: number
}

const formatRelativeTime = (date: Date | null): string => {
  if (!date) return 'No data'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMin / 60)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin} min ago`
  return `${diffHr} hr ago`
}

const getDataQuality = (
  date: Date | null
): { label: string; color: string } => {
  if (!date) return { label: 'No Data', color: 'bg-gray-400' }
  const now = new Date()
  const diffMin = (now.getTime() - date.getTime()) / 60000
  if (diffMin < 15) return { label: 'Normal', color: 'bg-green-500' }
  return { label: 'Stale', color: 'bg-yellow-500' }
}

const BatteryIcon: React.FC<{ percentage: number | null }> = ({ percentage }) => {
  const pct = percentage ?? 0
  const fillColor =
    pct > 50 ? 'var(--color-green-400)' : pct > 20 ? 'var(--color-yellow-500)' : 'var(--color-red-500)'
  const fillWidth = Math.max(0, Math.min(100, pct))

  return (
    <svg
      width="24"
      height="14"
      viewBox="0 0 24 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.5"
        y="0.5"
        width="20"
        height="13"
        rx="2.5"
        stroke="#6b7280"
        strokeWidth="1"
        fill="none"
      />
      <rect
        x="2"
        y="2"
        width={((fillWidth / 100) * 17).toFixed(1)}
        height="10"
        rx="1"
        fill={fillColor}
      />
      <rect x="21" y="4" width="2" height="6" rx="1" fill="#6b7280" />
    </svg>
  )
}

interface DeviceDashboardTabProps {
  deviceId: string
}

export const DeviceDashboardTab: React.FC<DeviceDashboardTabProps> = ({ deviceId }) => {
  const [phData, setPhData] = useState<PHData[]>([])
  const [currentPH, setCurrentPH] = useState(0)
  const [averagePH, setAveragePH] = useState(0)
  const [temperatureData, setTemperatureData] = useState<TemperatureData[]>([])
  const [currentTemp, setCurrentTemp] = useState(0)
  const [averageTemp, setAverageTemp] = useState(0)
  const [batteryVoltage, setBatteryVoltage] = useState('0V')
  const [batteryPercentage, setBatteryPercentage] = useState<number | null>(null)
  const [lastDataReceived, setLastDataReceived] = useState<Date | null>(null)
  const [isInMouth, setIsInMouth] = useState<boolean | null>(null)
  const [deviceMode, setDeviceMode] = useState<'live' | 'monitoring' | null>(null)

  const fetchDashboardData = async () => {
    if (!deviceId) return

    try {
      // Fetch last 5 minutes for dashboard
      const fiveMinutesAgo = new Date(Date.now() - 300000).toISOString()

      const { data, error } = await supabase
        .from('device_data')
        .select('*')
        .eq('device_id', deviceId)
        .gte('recorded_at', fiveMinutesAgo)
        .order('recorded_at', { ascending: true })

      if (error) {
        console.error('Error fetching dashboard data:', error)
        return
      }

      if (data && data.length > 0) {
        // Filter to monitoring mode (mode=2) for charts only
        const monitoringData = data.filter((row: DeviceDataRow) => row.mode === 2)

        // Process monitoring data for charts
        const phChartData: PHData[] = []
        const tempChartData: TemperatureData[] = []

        monitoringData.forEach((row: DeviceDataRow) => {
          const timestamp = new Date(row.recorded_at).getTime()

          phChartData.push({
            time: timestamp,
            value: row.calibrated_ph
          })

          tempChartData.push({
            time: timestamp,
            value: row.calibrated_temp
          })
        })

        setPhData(phChartData)
        setTemperatureData(tempChartData)

        // Update current/average values from monitoring data only
        if (phChartData.length > 0) {
          const latestPh = phChartData[phChartData.length - 1].value
          setCurrentPH(Number(latestPh.toFixed(1)))

          const avgPh =
            phChartData.reduce((sum, d) => sum + d.value, 0) / phChartData.length
          setAveragePH(Number(avgPh.toFixed(1)))
        }

        if (tempChartData.length > 0) {
          const latestTemp = tempChartData[tempChartData.length - 1].value
          setCurrentTemp(Number(latestTemp.toFixed(1)))

          const avgTemp =
            tempChartData.reduce((sum, d) => sum + d.value, 0) /
            tempChartData.length
          setAverageTemp(Number(avgTemp.toFixed(1)))
        }

        // Status bar uses ALL data (any mode) for real-time status
        const latestReading = data[data.length - 1] as DeviceDataRow
        const recordedAt = new Date(latestReading.recorded_at)
        const isWithinHour = (Date.now() - recordedAt.getTime()) < 3600000

        setBatteryVoltage(
          `${(latestReading.battery_voltage_mv / 1000).toFixed(2)}V`
        )
        setBatteryPercentage(
          isWithinHour ? Math.round((latestReading.battery_voltage_mv / 3000) * 100) : null
        )
        setLastDataReceived(recordedAt)

        // In Mouth: temperature 36-38°C
        const tempCelsius = latestReading.calibrated_temp
        setIsInMouth(isWithinHour ? (tempCelsius >= 36 && tempCelsius <= 38) : null)

        // Mode: 1=Calibration(Live), 2=Patient(Monitoring)
        setDeviceMode(isWithinHour ? (latestReading.mode === 1 ? 'live' : 'monitoring') : null)
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err)
    }
  }

  // Initial dashboard data load
  useEffect(() => {
    fetchDashboardData()
  }, [deviceId])

  // Poll for new data every 1 second
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData()
    }, 1000)

    return () => clearInterval(interval)
  }, [deviceId])

  // Check if data is within last hour for status display
  const hasRecentData = lastDataReceived && (Date.now() - lastDataReceived.getTime()) < 3600000

  return (
    <>
      {/* Top card - Device Status Row */}
      <div className="bg-white rounded-3xl px-6 py-4 shadow-sm text-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Battery */}
          <div className="flex items-center gap-2">
            <BatteryIcon percentage={batteryPercentage} />
            <span className="font-semibold">
              {hasRecentData && batteryPercentage !== null ? `${batteryPercentage}%` : '–'}
            </span>
          </div>

          {/* Mode */}
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${hasRecentData && deviceMode ? (deviceMode === 'live' ? 'bg-orange-400' : 'bg-green-400') : 'bg-gray-300'}`} />
            <span className="font-semibold">
              {hasRecentData && deviceMode ? (deviceMode === 'live' ? 'Live Mode' : 'Monitoring Mode') : '–'}
            </span>
          </div>

          {/* In Mouth */}
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${hasRecentData && isInMouth !== null ? (isInMouth ? 'bg-green-400' : 'bg-gray-300') : 'bg-gray-300'}`} />
            <span className="font-semibold">
              {hasRecentData && isInMouth !== null ? (isInMouth ? 'In Mouth' : 'Out of Mouth') : '–'}
            </span>
          </div>

          {/* Data Quality */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Data Quality:</span>
            <span className={`font-semibold ${getDataQuality(lastDataReceived).label === 'Stale' ? 'text-yellow-600' : ''}`}>
              {getDataQuality(lastDataReceived).label}
            </span>
          </div>

          {/* Last Data Received */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Last Data Received:</span>
            <span className="font-semibold">{formatRelativeTime(lastDataReceived)}</span>
          </div>
        </div>
      </div>

      {/* Bottom two cards - pH and Temperature */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* pH Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <h3
            className="text-xl font-semibold text-gray-800 mb-6"
            style={{ fontFamily: 'Family' }}
          >
            pH Monitoring
          </h3>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-2xs">
              <PHChart data={phData} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoCard
                title="Current pH"
                value={currentPH.toFixed(1)}
                nested
              />
              <InfoCard
                title="Average pH"
                value={averagePH.toFixed(1)}
                nested
              />
            </div>

            <DataTable
              title="Last 10 pH Readings"
              headers={['Timestamp', 'mV', 'pH']}
              data={phData.slice(-10).map(reading => ({
                timestamp: new Date(reading.time).toLocaleTimeString(),
                mV: `${(reading.value * 59.16).toFixed(1)}`,
                value: reading.value.toFixed(1)
              }))}
              batteryVoltage={batteryVoltage}
              nested
            />
          </div>
        </div>

        {/* Temperature Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <h3
            className="text-xl font-semibold text-gray-800 mb-6"
            style={{ fontFamily: 'Family' }}
          >
            Temperature Monitoring
          </h3>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-2xs">
              <TemperatureChart data={temperatureData} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoCard
                title="Current Temp"
                value={`${currentTemp.toFixed(1)}°C`}
                nested
              />
              <InfoCard
                title="Average Temp"
                value={`${averageTemp.toFixed(1)}°C`}
                nested
              />
            </div>

            <DataTable
              title="Last 10 Temperature Readings"
              headers={['Timestamp', 'mV', '°C']}
              data={temperatureData.slice(-10).map(reading => ({
                timestamp: new Date(reading.time).toLocaleTimeString(),
                mV: `${(reading.value * 27.3).toFixed(1)}`,
                value: reading.value.toFixed(1)
              }))}
              batteryVoltage={batteryVoltage}
              nested
            />
          </div>
        </div>
      </div>
    </>
  )
}
